import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import { inspeccionesService, descargarReportePDF } from '../services/inspecciones';
import toast from 'react-hot-toast';
import {
    ArrowLeft,
    History,
    Filter,
    Download,
    Calendar,
    CheckCircle2,
    Clock,
    AlertCircle,
    FileText,
} from 'lucide-react';

const MESES = [
    { v: 1, n: 'Enero' }, { v: 2, n: 'Febrero' }, { v: 3, n: 'Marzo' },
    { v: 4, n: 'Abril' }, { v: 5, n: 'Mayo' }, { v: 6, n: 'Junio' },
    { v: 7, n: 'Julio' }, { v: 8, n: 'Agosto' }, { v: 9, n: 'Septiembre' },
    { v: 10, n: 'Octubre' }, { v: 11, n: 'Noviembre' }, { v: 12, n: 'Diciembre' },
];

const formatPeriodo = (item) => {
    const mes = MESES.find((m) => m.v === item.mes)?.n || item.mes;
    const q = item.quincena === 'Q1' ? '1ra quincena' : '2da quincena';
    return `${q} — ${mes} ${item.anio}`;
};

const HistorialInspeccionesPage = () => {
    const navigate = useNavigate();
    const anioActual = new Date().getFullYear();

    const [filtros, setFiltros] = useState({ anio: anioActual, mes: '', dia: '' });
    const [cargando, setCargando] = useState(false);
    const [items, setItems] = useState([]);
    const [descargandoId, setDescargandoId] = useState(null);

    const aniosDisponibles = useMemo(() => {
        const arr = [];
        for (let y = anioActual; y >= anioActual - 5; y--) arr.push(y);
        return arr;
    }, [anioActual]);

    const cargar = async () => {
        setCargando(true);
        try {
            const data = await inspeccionesService.historial({
                anio: filtros.anio || null,
                mes: filtros.mes || null,
                dia: filtros.dia || null,
            });
            setItems(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al cargar el historial');
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => { cargar(); }, []);

    const handleAplicarFiltros = () => cargar();

    const handleLimpiarFiltros = () => {
        setFiltros({ anio: '', mes: '', dia: '' });
        setTimeout(cargar, 0);
    };

    const handleDescargarPDF = async (sesion) => {
        setDescargandoId(sesion);
        try {
            await descargarReportePDF(sesion);
            toast.success('Reporte descargado');
        } catch {
            toast.error('Error al descargar el reporte');
        } finally {
            setDescargandoId(null);
        }
    };

    const handleVerDetalle = (sesion) => {
        navigate(`/inspecciones?sesion=${encodeURIComponent(sesion)}`);
    };

    return (
        <Layout>
            <div className="p-6">
                {/* Header */}
                <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/inspecciones')}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Volver"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                                <History className="h-7 w-7 mr-2 text-blue-600" />
                                Historial de Inspecciones
                            </h1>
                            <p className="text-gray-500 text-sm mt-1">
                                Sesiones quincenales anteriores con su resumen.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Filtros */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
                    <div className="flex items-center mb-4">
                        <Filter className="h-4 w-4 text-gray-500 mr-2" />
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                            Filtrar por fecha
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Año</label>
                            <select
                                value={filtros.anio}
                                onChange={(e) => setFiltros({ ...filtros, anio: e.target.value, mes: '', dia: '' })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Todos</option>
                                {aniosDisponibles.map((y) => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Mes</label>
                            <select
                                value={filtros.mes}
                                onChange={(e) => setFiltros({ ...filtros, mes: e.target.value, dia: '' })}
                                disabled={!filtros.anio}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                            >
                                <option value="">Todos</option>
                                {MESES.map((m) => (
                                    <option key={m.v} value={m.v}>{m.n}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Día</label>
                            <select
                                value={filtros.dia}
                                onChange={(e) => setFiltros({ ...filtros, dia: e.target.value })}
                                disabled={!filtros.mes}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                            >
                                <option value="">Todos</option>
                                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-end gap-2">
                            <button
                                onClick={handleAplicarFiltros}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
                            >
                                Aplicar
                            </button>
                            <button
                                onClick={handleLimpiarFiltros}
                                className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                            >
                                Limpiar
                            </button>
                        </div>
                    </div>
                </div>

                {/* Lista */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <h2 className="text-base font-semibold text-gray-700 mb-4">
                        Resultados — {items.length} sesión{items.length !== 1 ? 'es' : ''}
                    </h2>

                    {cargando && (
                        <div className="flex justify-center py-10">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                        </div>
                    )}

                    {!cargando && items.length === 0 && (
                        <div className="text-center py-14 text-gray-400">
                            <FileText className="h-14 w-14 mx-auto mb-3 opacity-20" />
                            <p className="text-sm font-medium">No hay inspecciones registradas para los filtros aplicados.</p>
                        </div>
                    )}

                    {!cargando && items.map((item) => (
                        <div
                            key={item.sesion}
                            className="border border-gray-200 rounded-xl p-4 mb-3 hover:shadow-sm hover:border-blue-300 transition-all"
                        >
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Calendar className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                        <h3 className="text-sm font-bold text-gray-800 truncate">
                                            {formatPeriodo(item)}
                                        </h3>
                                        <span className="px-2 py-0.5 text-xs font-mono bg-blue-50 text-blue-700 rounded">
                                            {item.sesion}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500 ml-6">
                                        {item.ultima_actividad
                                            ? `Última actividad: ${new Date(item.ultima_actividad).toLocaleString('es-PE')}`
                                            : 'Sin actividad'}
                                    </p>
                                </div>

                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="flex items-center gap-1 px-2.5 py-1 bg-green-50 border border-green-200 rounded-lg">
                                        <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                        <span className="text-xs font-semibold text-green-700">{item.total_ok} OK</span>
                                    </div>
                                    <div className="flex items-center gap-1 px-2.5 py-1 bg-yellow-50 border border-yellow-200 rounded-lg">
                                        <Clock className="h-3.5 w-3.5 text-yellow-600" />
                                        <span className="text-xs font-semibold text-yellow-700">{item.total_pendiente} Pend</span>
                                    </div>
                                    <div className="flex items-center gap-1 px-2.5 py-1 bg-red-50 border border-red-200 rounded-lg">
                                        <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                                        <span className="text-xs font-semibold text-red-700">{item.total_observado} Obs</span>
                                    </div>
                                    <div className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-lg">
                                        <span className="text-xs font-bold text-blue-700">{item.porcentaje_completado}%</span>
                                    </div>

                                    <button
                                        onClick={() => handleVerDetalle(item.sesion)}
                                        className="px-3 py-1.5 text-xs font-semibold text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                                    >
                                        Ver
                                    </button>
                                    <button
                                        onClick={() => handleDescargarPDF(item.sesion)}
                                        disabled={descargandoId === item.sesion}
                                        className="flex items-center px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 disabled:opacity-60 transition-colors"
                                    >
                                        {descargandoId === item.sesion ? (
                                            <div className="animate-spin rounded-full h-3 w-3 border-b border-white mr-1" />
                                        ) : (
                                            <Download className="h-3.5 w-3.5 mr-1" />
                                        )}
                                        PDF
                                    </button>
                                </div>
                            </div>

                            <div className="mt-3 w-full bg-gray-200 rounded-full h-1.5">
                                <div
                                    className="bg-green-500 h-1.5 rounded-full transition-all duration-500"
                                    style={{ width: `${item.porcentaje_completado}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </Layout>
    );
};

export default HistorialInspeccionesPage;
